import keycode from 'keycode';
import type { TFocusEvent } from '@alfa-bank/site-input/models';
import type { KeyboardEvent } from 'react';

import type { TOptions, IOption, TSelect, TSelectEventHandler } from '../models';
import type { ISelectEvent as IAutocompleteOnSelectParam } from '../../autocomplete/models';

export const getHighlightedIndex = (inputValue = '', options: TOptions = []) =>
    options.findIndex(({ label }) => label === inputValue);

export const getDownIndex = ({
    highlightedItem,
    options,
}: {
    highlightedItem: number;
    options: TOptions;
}) => {
    if (highlightedItem < options.length - 1) {
        return highlightedItem + 1;
    }

    return highlightedItem;
};

const keyUp = ({
    highlightedItem,
    setHighlightedItem,
}: {
    highlightedItem: number;
    setHighlightedItem: (value: number) => void;
}) => {
    if (highlightedItem > 0) {
        setHighlightedItem(highlightedItem - 1);
    }
};

const keyDown = ({
    setHighlightedItem,
    ...rest
}: {
    highlightedItem: number;
    options: TOptions;
    setHighlightedItem: (value: number) => void;
}) => setHighlightedItem(getDownIndex(rest));

export const onDropdownSelect =
    ({
        name,
        onSelect,
        options,
        setHighlightedItem,
        setInputValue,
        setIsOpen,
    }: {
        name: string;
        onSelect: TSelectEventHandler;
        options: TOptions;
        setHighlightedItem: (value: number) => void;
        setInputValue: (value: string) => void;
        setIsOpen: (value: boolean) => void;
    }) =>
    ({ event, value = {} as IOption }: IAutocompleteOnSelectParam) => {
        event.preventDefault();
        const { label } = value;
        const res = { ...value, name, value: label, event };

        onSelect(res);
        setInputValue(label);
        setHighlightedItem(getHighlightedIndex(label, options));

        setTimeout(() => {
            setIsOpen(false);
        }, 0);
    };

export const onInputKeyDown =
    ({
        highlightedItem,
        name,
        onSelect,
        options,
        setHighlightedItem,
        setInputValue,
        setIsOpen,
    }: {
        highlightedItem: number;
        name: string;
        onSelect: TSelectEventHandler;
        options: TOptions;
        setHighlightedItem: (value: number) => void;
        setInputValue: (value: string) => void;
        setIsOpen: (value: boolean) => void;
    }) =>
    ({ event }: { event: KeyboardEvent<HTMLInputElement | HTMLLIElement> }) => {
        if (event.keyCode === keycode('Esc')) {
            return setIsOpen(false);
        }

        if (event.keyCode === keycode('Up')) {
            event.preventDefault();

            return keyUp({ highlightedItem, setHighlightedItem });
        }

        if (event.keyCode === keycode('Down')) {
            event.preventDefault();

            return keyDown({ highlightedItem, setHighlightedItem, options });
        }

        /*
         * Из-за этого хром на маке почему-то не обрезал пробелы по краям на onBlur.
         * if (event.keyCode === keycode('Space')) {
         *     return setIsOpen(!isOpen);
         * }
         */

        const selectedOption = options[highlightedItem];

        if (event.keyCode === keycode('Enter') && selectedOption) {
            return onDropdownSelect({
                name,
                options,
                onSelect,
                setInputValue,
                setIsOpen,
                setHighlightedItem,
            })({
                value: selectedOption,
                event,
            });
        }

        return null;
    };

export const callIfBlurred = (wrapperRef: React.RefObject<HTMLDivElement>, cb: () => void) => {
    const blurOnTimeout = () => {
        const focusInside = wrapperRef.current?.contains(document.activeElement);

        if (!focusInside) {
            cb();
        }
    };

    setTimeout(blurOnTimeout, 0);
};

export const onBlurInput =
    ({
        onBlur,
        setIsOpen,
        wrapperRef,
    }: {
        onBlur: NonNullable<TSelect['onBlur']>;
        setIsOpen: (value: boolean) => void;
        wrapperRef?: React.RefObject<HTMLDivElement>;
    }) =>
    (event: TFocusEvent) => {
        if (!wrapperRef?.current) {
            return;
        }

        callIfBlurred(wrapperRef, () => {
            setIsOpen(false);
            onBlur(event);
        });
    };
